import React, { useEffect, useRef, useState } from 'react';
import { api, CacheProvider, CachePurgeStrategy, ComponentCacheConfig, DependentCachePolicy, ProjectApplication, UpdateComponentSettingsInput } from '../api';
import { cacheProviderLabel, cachePurgeStrategyLabel, dependentCachePolicyLabel } from '../labels';
import { Checkbox, Dialog, Field, Input, Select, Subsection, Textarea } from './primitives';
import { ErrorNotice } from './ui';
import { BuildSettingsFields, buildSettingsInput, buildSettingsOf, useFrontendFrameworks } from './BuildSettingsFields';
import type { BuildSettings } from './BuildSettingsFields';
import { CloudflareSetup, isZoneId } from './CloudflareSetup';

interface ComponentSettingsModalProps {
  projectId: string;
  application: ProjectApplication;
  /** The project's other components, for dependencies. */
  others: ProjectApplication[];
  onClose: () => void;
  onSaved: (updated: ProjectApplication) => void;
  /** Opens this frontend's variables in the environment shown, when there is one. */
  onEditVariables?: () => void;
  /** The environment whose variables `onEditVariables` opens. */
  environmentName?: string;
  /** The environment whose secrets hold this component's cache token; without it the token cannot be managed here. */
  environmentId?: string;
}

/**
 * The build settings of one frontend (CO-159): the one dialog behind every
 * "Edit build settings". Repository, build and start (the shared
 * BuildSettingsFields, CO-90), health checks, what it needs at runtime, its
 * cache and whether automation runs for it — grouped, two columns of short
 * fields on a wide screen, the mechanisms behind named help.
 */
export function ComponentSettingsModal({ projectId, application: app, others, onClose, onSaved, onEditVariables, environmentName, environmentId }: ComponentSettingsModalProps) {
  const frameworks = useFrontendFrameworks();
  const [name, setName] = useState(app.name);
  const [branch, setBranch] = useState(app.default_branch ?? '');
  const [repoUrl, setRepoUrl] = useState(app.repository_url);
  const [build, setBuild] = useState<BuildSettings>(() => buildSettingsOf(app));
  const [healthPath, setHealthPath] = useState(app.health_check?.path ?? '');
  const [healthStatus, setHealthStatus] = useState(app.health_check?.expected_status ? String(app.health_check.expected_status) : '');
  const [healthContent, setHealthContent] = useState(app.health_check?.expected_content ?? '');
  const [backendPath, setBackendPath] = useState(app.health_check?.backend_path ?? '');
  const [backendContent, setBackendContent] = useState(app.health_check?.backend_expected_content ?? '');
  const [dependsOn, setDependsOn] = useState<string[]>(app.depends_on ?? []);
  const [disabled, setDisabled] = useState(Boolean(app.disabled));
  const cache = app.cache;
  const [provider, setProvider] = useState<CacheProvider>(cache?.provider ?? 'none');
  const [strategy, setStrategy] = useState<CachePurgeStrategy>(cache?.purge_strategy ?? 'full_component');
  const [automaticPurge, setAutomaticPurge] = useState(cache?.automatic_purge ?? true);
  const [afterBackend, setAfterBackend] = useState<DependentCachePolicy>(cache?.purge_after_backend_deploy ?? 'never');
  const [purgeAliases, setPurgeAliases] = useState(Boolean(cache?.purge_aliases));
  const [zoneId, setZoneId] = useState(cache?.cloudflare_zone_id ?? '');
  const [zoneName, setZoneName] = useState(cache?.cloudflare_zone_name ?? '');
  const [verifyEdge, setVerifyEdge] = useState(Boolean(cache?.verify_edge));
  const [endpointUrl, setEndpointUrl] = useState(cache?.custom_endpoint_url ?? '');
  const [endpointHeader, setEndpointHeader] = useState(cache?.custom_endpoint_header ?? '');
  const [varnishEndpoint, setVarnishEndpoint] = useState(cache?.varnish_endpoint ?? '');
  const [varnishMethod, setVarnishMethod] = useState(cache?.varnish_method ?? 'PURGE');
  const [warmupEnabled, setWarmupEnabled] = useState(Boolean(cache?.warmup_enabled));
  const [warmupPaths, setWarmupPaths] = useState((cache?.warmup_paths ?? ['/']).join('\n'));
  const [warmupSitemap, setWarmupSitemap] = useState(Boolean(cache?.warmup_sitemap));
  const [warmupSitemapPath, setWarmupSitemapPath] = useState(cache?.warmup_sitemap_path ?? '');
  const [warmupMaxUrls, setWarmupMaxUrls] = useState(cache?.warmup_max_urls ? String(cache.warmup_max_urls) : '');
  const [speedCheck, setSpeedCheck] = useState(!cache?.latency_budget?.disabled);
  const [speedPercent, setSpeedPercent] = useState(cache?.latency_budget?.max_increase_percent ? String(cache.latency_budget.max_increase_percent) : '');
  const [speedMs, setSpeedMs] = useState(cache?.latency_budget?.min_increase_ms ? String(cache.latency_budget.min_increase_ms) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // The body scrolls; a refused save is brought into view.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error]);

  const backendId = app.backend_application_id;
  const candidates = others.filter((o) => o.id !== backendId);
  // The Magento application has its own build and deployment pages; here it
  // has only its cache (CO-201).
  const cacheOnly = app.type !== 'frontend';
  const providers: CacheProvider[] = cacheOnly ? ['none', 'cloudflare', 'custom_endpoint'] : ['none', 'cloudflare', 'varnish', 'custom_endpoint'];
  // What is saved: the cache settings as they were when the dialog opened, or
  // as the last "Save zone" left them. A form that differs from it is dirty.
  const [savedCache, setSavedCache] = useState(cache);
  const [savedApp, setSavedApp] = useState<ProjectApplication | null>(null);
  const [baseline, setBaseline] = useState<string | null>(null);

  function buildCacheInput(): ComponentCacheConfig {
    return {
      provider,
      purge_strategy: strategy,
      automatic_purge: automaticPurge,
      purge_after_backend_deploy: afterBackend,
      purge_aliases: purgeAliases,
      cloudflare_zone_id: zoneId.trim() || undefined,
      cloudflare_zone_name: provider === 'cloudflare' && zoneName ? zoneName : undefined,
      verify_edge: provider === 'cloudflare' && verifyEdge ? true : undefined,
      custom_endpoint_url: endpointUrl.trim() || undefined,
      custom_endpoint_header: endpointHeader.trim() || undefined,
      varnish_endpoint: varnishEndpoint.trim() || undefined,
      varnish_method: varnishMethod || undefined,
      warmup_enabled: warmupEnabled,
      warmup_paths: warmupPaths.split('\n').map((p) => p.trim()).filter(Boolean),
      warmup_sitemap: warmupEnabled && warmupSitemap,
      warmup_sitemap_path: warmupSitemapPath.trim() || undefined,
      warmup_max_urls: warmupMaxUrls.trim() ? Number(warmupMaxUrls) : undefined,
      latency_budget: {
        disabled: !speedCheck || undefined,
        max_increase_percent: speedCheck && speedPercent.trim() ? Number(speedPercent) : undefined,
        min_increase_ms: speedCheck && speedMs.trim() ? Number(speedMs) : undefined,
      },
    };
  }
  useEffect(() => {
    setBaseline(JSON.stringify(buildCacheInput()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dirty = baseline !== null && JSON.stringify(buildCacheInput()) !== baseline;

  function numberError(): Error | null {
    if (warmupEnabled && warmupSitemap && warmupMaxUrls.trim() && !/^\d+$/.test(warmupMaxUrls.trim())) {
      return new Error('Most pages to warm must be a whole number between 1 and 500.');
    }
    if (speedCheck && ((speedPercent.trim() && !/^\d+$/.test(speedPercent.trim())) || (speedMs.trim() && !/^\d+$/.test(speedMs.trim())))) {
      return new Error('The speed limits must be whole numbers.');
    }
    return null;
  }

  // Saves the cache settings alone and keeps the dialog open, so the zone can
  // be tested with a real purge before the dialog is closed.
  async function saveCacheOnly() {
    const problem = numberError();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const updated = await api.updateComponentSettings(projectId, app.id, { cache: buildCacheInput() });
      setSavedCache(updated.cache);
      setSavedApp(updated);
      setBaseline(JSON.stringify(buildCacheInput()));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSaving(false);
    }
  }

  // Closing after a "Save zone" refreshes what is behind the dialog.
  const close = () => (savedApp ? onSaved(savedApp) : onClose());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const problem = numberError();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    const input: UpdateComponentSettingsInput = cacheOnly
      ? {}
      : {
          name: name.trim(),
          default_branch: branch.trim(),
          repository_url: repoUrl.trim(),
          ...buildSettingsInput(build),
          depends_on: dependsOn,
          disabled,
        };
    input.cache = buildCacheInput();
    if (cacheOnly) {
      // Nothing else of the Magento application is edited here.
    } else if (healthPath.trim() || backendPath.trim()) {
      input.health_check = {
        path: healthPath.trim(),
        expected_status: healthStatus.trim() ? Number(healthStatus) : undefined,
        expected_content: healthContent.trim() || undefined,
        backend_path: backendPath.trim() || undefined,
        backend_expected_content: backendContent.trim() || undefined,
      };
    } else {
      input.clear_health_check = true;
    }
    try {
      const updated = await api.updateComponentSettings(projectId, app.id, input);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      size="lg"
      title={cacheOnly ? `${app.name} cache` : `${app.name} build settings`}
      description={cacheOnly ? undefined : 'Applies from the next deployment; the live release is not touched.'}
      onClose={close}
      onSubmit={submit}
      busy={saving}
      testId="component-settings-modal"
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={close} disabled={saving}>
            {savedApp ? 'Done' : 'Cancel'}
          </button>
          <button type="submit" className="button button--primary" disabled={saving} data-testid="component-settings-save">
            {saving ? 'Saving' : cacheOnly ? 'Save cache settings' : 'Save build settings'}
          </button>
        </>
      }
    >
      <div ref={errorRef}>
        <ErrorNotice error={error} />
      </div>
      <div className="build-form build-form--columns">
        {!cacheOnly ? (
          <>
            <Subsection title="Repository">
              <div className="build-form__grid">
                <Field label="Name">
                  <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" data-testid="component-settings-name" />
                </Field>
                <Field label="Branch to deploy">
                  <Input type="text" className="input--mono" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" spellCheck={false} autoComplete="off" />
                </Field>
                <label className="field build-form__wide">
                  <span className="field__label">Repository URL</span>
                  <Input type="text" className="input--mono" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} spellCheck={false} autoComplete="off" />
                </label>
              </div>
            </Subsection>

            <Subsection title="Build and start">
              <BuildSettingsFields value={build} onChange={setBuild} frameworks={frameworks} testIdPrefix="component-settings" disabled={saving} />
            </Subsection>

            <Subsection
              title="Health checks"
            >
              <div className="build-form__grid">
                <Field label="Path">
                  <Input type="text" className="input--mono" value={healthPath} onChange={(e) => setHealthPath(e.target.value)} placeholder="/ (default)" spellCheck={false} autoComplete="off" data-testid="component-settings-health" />
                </Field>
                <Field label="Expected status">
                  <Input type="text" inputMode="numeric" value={healthStatus} onChange={(e) => setHealthStatus(e.target.value)} placeholder="200" autoComplete="off" />
                </Field>
                <label className="field build-form__wide">
                  <span className="field__label">Response must contain</span>
                  <Input type="text" value={healthContent} onChange={(e) => setHealthContent(e.target.value)} placeholder="Optional" autoComplete="off" />
                </label>
                <Field
                  label="Backend route"
                  hint="A route that renders from Magento; required when the frontend uses Magento."
                >
                  <Input
                    type="text"
                    className="input--mono"
                    value={backendPath}
                    onChange={(e) => setBackendPath(e.target.value)}
                    placeholder="/api/backend"
                    spellCheck={false}
                    autoComplete="off"
                    data-testid="component-settings-backend-path"
                  />
                </Field>
                <Field label="Backend route must contain">
                  <Input type="text" value={backendContent} onChange={(e) => setBackendContent(e.target.value)} placeholder="The store view code" autoComplete="off" />
                </Field>
              </div>
            </Subsection>

            <Subsection
              title="Runtime dependencies"
              description="The Magento backend is always required."
              actions={
                onEditVariables ? (
                  <button type="button" className="button button--secondary button--small" onClick={onEditVariables} data-testid="component-settings-variables">
                    {environmentName ? `Edit variables in ${environmentName}` : 'Edit variables'}
                  </button>
                ) : undefined
              }
            >
              {candidates.length === 0 ? (
                <p className="build-form__note">No other components to depend on.</p>
              ) : (
                <ul className="build-form__checks">
                  {candidates.map((o) => (
                    <li key={o.id}>
                      <Checkbox
                        checked={dependsOn.includes(o.id)}
                        onChange={(e) => setDependsOn((d) => (e.target.checked ? [...d, o.id] : d.filter((x) => x !== o.id)))}
                        label={<>{o.name}</>}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Subsection>

          </>
        ) : null}

        <Subsection
          title="Cache"
          description={cacheOnly ? 'Storefront hostnames only.' : `A release invalidates only ${app.name}’s own hostnames; the other storefronts are never touched.`}
          testId="component-cache-settings"
        >
          <div className="build-form__grid">
            <Field label="Provider" hint="Without one, releases go live without an external purge.">
              <Select value={provider} onChange={(e) => setProvider(e.target.value as CacheProvider)} data-testid="cache-provider">
                {providers.map((p) => (
                  <option key={p} value={p}>{cacheProviderLabel[p]}</option>
                ))}
              </Select>
            </Field>
            <Field label="What to invalidate">
              <Select value={strategy} onChange={(e) => setStrategy(e.target.value as CachePurgeStrategy)} disabled={provider === 'none'} data-testid="cache-strategy">
                {(['full_component', 'changed_urls', 'none'] as CachePurgeStrategy[]).map((sType) => (
                  <option key={sType} value={sType}>{cacheOnly && sType === 'full_component' ? 'Everything on its hostnames' : cachePurgeStrategyLabel[sType]}</option>
                ))}
              </Select>
            </Field>
            {!cacheOnly ? (
              <Field label="After a Magento release" hint="A Magento release never rebuilds this storefront.">
                <Select value={afterBackend} onChange={(e) => setAfterBackend(e.target.value as DependentCachePolicy)} disabled={provider === 'none'} data-testid="cache-after-backend">
                  {(['never', 'always'] as DependentCachePolicy[]).map((p) => (
                    <option key={p} value={p}>{dependentCachePolicyLabel[p]}</option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {provider === 'cloudflare' && !environmentId ? (
              <Field label="Cloudflare zone" hint={<>The API token is this component’s <code>CACHE_PURGE_CREDENTIAL</code> secret.</>}>
                <Input type="text" className="input--mono" value={zoneId} onChange={(e) => setZoneId(e.target.value)} placeholder="Zone ID of the storefront’s domain" spellCheck={false} autoComplete="off" data-testid="cache-zone" />
              </Field>
            ) : null}
            {provider === 'cloudflare' && environmentId ? (
              <CloudflareSetup
                projectId={projectId}
                applicationId={app.id}
                environmentId={environmentId}
                zoneId={zoneId}
                zoneName={zoneName}
                onZone={(id, name) => {
                  setZoneId(id);
                  setZoneName(name);
                }}
                savedZoneId={savedCache?.provider === 'cloudflare' && isZoneId(savedCache.cloudflare_zone_id ?? '') ? savedCache.cloudflare_zone_id : undefined}
                dirty={dirty}
                saving={saving}
                onSave={saveCacheOnly}
              />
            ) : null}
            {provider === 'custom_endpoint' ? (
              <>
                <Field label="Purge endpoint">
                  <Input type="text" className="input--mono" value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} placeholder="https://…" spellCheck={false} autoComplete="off" />
                </Field>
                <Field
                  label="Credential header"
                  hint={<>Its value is this component’s <code>CACHE_PURGE_CREDENTIAL</code> secret.</>}
                >
                  <Input type="text" className="input--mono" value={endpointHeader} onChange={(e) => setEndpointHeader(e.target.value)} placeholder="X-Purge-Token" spellCheck={false} autoComplete="off" />
                </Field>
              </>
            ) : null}
            {provider === 'varnish' ? (
              <>
                <Field label="Cache address">
                  <Input type="text" className="input--mono" value={varnishEndpoint} onChange={(e) => setVarnishEndpoint(e.target.value)} placeholder="varnish:6081 (blank: the storefront’s own hostname)" spellCheck={false} autoComplete="off" />
                </Field>
                <Field label="Request">
                  <Select value={varnishMethod} onChange={(e) => setVarnishMethod(e.target.value)}>
                    <option value="PURGE">PURGE</option>
                    <option value="BAN">BAN</option>
                  </Select>
                </Field>
              </>
            ) : null}
          </div>
          {provider !== 'none' ? (
            <div className="build-form__inline-checks">
              <Checkbox
                checked={automaticPurge}
                onChange={(e) => setAutomaticPurge(e.target.checked)}
                data-testid="cache-automatic"
                label="Invalidate automatically after a release"
              />
              <Checkbox
                checked={purgeAliases}
                onChange={(e) => setPurgeAliases(e.target.checked)}
                label="Also invalidate alias hostnames"
              />
              {provider === 'cloudflare' ? (
                <Checkbox
                  checked={verifyEdge}
                  onChange={(e) => setVerifyEdge(e.target.checked)}
                  data-testid="cache-verify-edge"
                  label="Record cf-cache-status after a purge"
                />
              ) : null}
            </div>
          ) : null}
          {!cacheOnly ? (
            <Checkbox
              checked={warmupEnabled}
              onChange={(e) => setWarmupEnabled(e.target.checked)}
              data-testid="cache-warmup"
              label="Warm these URLs after a release"
            />
          ) : null}
          {!cacheOnly && warmupEnabled ? (
            <Field
              label="URLs to warm"
              hint="One path per line on the canonical hostname."
            >
              <Textarea className="input--mono build-form__paths" value={warmupPaths} onChange={(e) => setWarmupPaths(e.target.value)} rows={4} spellCheck={false} data-testid="cache-warmup-paths" />
            </Field>
          ) : null}
          {!cacheOnly && warmupEnabled ? (
            <Checkbox
              checked={warmupSitemap}
              onChange={(e) => setWarmupSitemap(e.target.checked)}
              data-testid="cache-warmup-sitemap"
              label="Also warm the pages the sitemap lists"
              hint="Blue-green environments render them on the new release, before it takes traffic, so the first visitors get finished pages. Other environments warm the paths above after the release."
            />
          ) : null}
          {!cacheOnly && warmupEnabled && warmupSitemap ? (
            <div className="build-form__grid">
              <Field label="Sitemap path" hint="A sitemap or sitemap index on the canonical hostname.">
                <Input type="text" className="input--mono" value={warmupSitemapPath} onChange={(e) => setWarmupSitemapPath(e.target.value)} placeholder="/sitemap.xml" spellCheck={false} autoComplete="off" data-testid="cache-warmup-sitemap-path" />
              </Field>
              <Field label="Most pages to warm" hint="Counts the paths above too. Up to 500; each page is a render that calls Magento.">
                <Input type="text" inputMode="numeric" value={warmupMaxUrls} onChange={(e) => setWarmupMaxUrls(e.target.value)} placeholder="100" autoComplete="off" data-testid="cache-warmup-max-urls" />
              </Field>
            </div>
          ) : null}
        </Subsection>

        {!cacheOnly ? (
          <>
            <Subsection title="Speed check">
              <Checkbox
                checked={speedCheck}
                onChange={(e) => setSpeedCheck(e.target.checked)}
                data-testid="speed-check-enabled"
                label="Compare answer time before and after a release"
                hint="Reports only; never rolls back."
              />
              {speedCheck ? (
                <div className="build-form__grid">
                  <Field label="Slower by at least (%)" hint="Default 50.">
                    <Input type="text" inputMode="numeric" value={speedPercent} onChange={(e) => setSpeedPercent(e.target.value)} placeholder="50" autoComplete="off" data-testid="speed-check-percent" />
                  </Field>
                  <Field label="and by at least (ms)" hint="Default 300. Both must be passed.">
                    <Input type="text" inputMode="numeric" value={speedMs} onChange={(e) => setSpeedMs(e.target.value)} placeholder="300" autoComplete="off" data-testid="speed-check-ms" />
                  </Field>
                </div>
              ) : null}
            </Subsection>

            <Subsection title="Automation">
              <Checkbox
                id="component-settings-disabled"
                checked={disabled}
                onChange={(e) => setDisabled(e.target.checked)}
                data-testid="component-settings-disabled"
                label="Disabled"
                hint="Leaves this frontend out of deployments, Deploy all and monitoring. The site keeps running: only Remove stops it."
              />
            </Subsection>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
